/* ------------------------------------------------------------------
 *  HomePage — AY Solutions Academy
 *  Édition "Éditorial Academy" : layout magazine, typographie Fraunces,
 *  collage parallax, marquee, timeline pointillée, polaroids, stickers.
 * ------------------------------------------------------------------ */
import { useEffect, useState, useCallback, useMemo } from "react"
import { Link, useNavigate } from "react-router-dom"
import {
  ArrowUpRight,
  ArrowRight,
  BookOpen,
  Award,
  Clock,
  Users,
  Play,
  Quote,
  Star,
  GraduationCap,
  Monitor,
  Trophy,
} from "lucide-react"
import SearchBar from "../components/SearchBar"
import CourseGrid from "../components/course/CourseGrid"
import { useGet } from "../utils/apiComponent/useApi"
import { endpoints } from "../utils/endpoints"
import { directCoursesApiResponseSchema } from "../utils/schemas/cours-schema"
import { api } from "../utils/apiComponent/apiClient"
import { useTranslation } from "../i18n"
import { loadTranslationsForEntities } from "../utils/translationUtils"
import "../styles/home-editorial.css"


function Eyebrow({ children, className = "" }) {
  return (
    <span
      className={`inline-block text-[11px] sm:text-xs uppercase tracking-[0.28em] text-[#6B7280] font-sans ${className}`}
    >
      {children}
    </span>
  )
}

function useReveal(deps = []) {
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible")
            obs.unobserve(e.target)
          }
        })
      },
      { threshold: 0.12 }
    )
    const observeAll = () => {
      document.querySelectorAll(".reveal:not(.is-visible)").forEach((el) => obs.observe(el))
    }
    observeAll()
    const mo = new MutationObserver(() => observeAll())
    mo.observe(document.body, { childList: true, subtree: true })
    return () => {
      obs.disconnect()
      mo.disconnect()
    }
  }, deps)
}
export default function HomePage() {
  const { tObj, language } = useTranslation()
  const navigate = useNavigate()
  const [categories, setCategories] = useState([])
  const [categoriesLoaded, setCategoriesLoaded] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [testimonials, setTestimonials] = useState([])

  useReveal()

  const {
    execute: getPopularCourses,
    data: apiResponse,
    loading: loadingCourses,
  } = useGet(endpoints.courses, {
    schema: directCoursesApiResponseSchema,
    isPublicRoute: true,
  })

  useEffect(() => {
    getPopularCourses()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const baseCourses = useMemo(() => {
    return apiResponse?.courses || []
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiResponse?.courses?.map((c) => c?.id).join(",")])

  const [translatedCourses, setTranslatedCourses] = useState([])
  useEffect(() => {
    if (baseCourses.length > 0 && language) {
      loadTranslationsForEntities("course", baseCourses, language)
        .then(setTranslatedCourses)
        .catch(() => setTranslatedCourses(baseCourses))
    } else {
      setTranslatedCourses(baseCourses)
    }
  }, [baseCourses, language])

  const allCourses = translatedCourses
  const recommendedCourses = allCourses.slice(0, 12)

  const loadCategoriesIfNeeded = useCallback(async () => {
    if (categoriesLoaded || categories.length > 0) return
    try {
      const params = new URLSearchParams()
      params.append("language", language || "fr")
      const url = `${endpoints.getCategories}?${params.toString()}`
      const response = await api.get(url, { isPublicRoute: true })
      const data = response?.data || response
      const arr = Array.isArray(data)
        ? data
        : data?.categories && Array.isArray(data.categories)
        ? data.categories
        : []
      if (arr.length > 0) setCategories(arr)
      setCategoriesLoaded(true)
    } catch {
      setCategoriesLoaded(true)
    }
  }, [categoriesLoaded, categories.length, language])

  const loadTestimonials = useCallback(async () => {
    try {
      const params = new URLSearchParams()
      params.append("isFeatured", "true")
      params.append("limit", "6")
      const url = `${endpoints.testimonialsPublished}?${params.toString()}`
      const response = await api.get(url, { isPublicRoute: true })
      const data = response?.data || response
      const arr = Array.isArray(data)
        ? data
        : data?.testimonials && Array.isArray(data.testimonials)
        ? data.testimonials
        : data?.items && Array.isArray(data.items)
        ? data.items
        : []
      setTestimonials(arr)
    } catch {
      setTestimonials([])
    }
  }, [])

  useEffect(() => {
    loadTestimonials()
  }, [loadTestimonials])

  // suppress unused var warning
  void loadCategoriesIfNeeded

  const handleSearch = (term) => {
    setSearchTerm(term || "")
    if (!term || !term.trim()) {
      navigate("/courses")
      return
    }
    navigate(`/courses?search=${encodeURIComponent(term.trim())}`)
  }

  const handleExploreCourses = () => {
    if (searchTerm && searchTerm.trim()) {
      navigate(`/courses?search=${encodeURIComponent(searchTerm.trim())}`)
    } else {
      navigate("/courses")
    }
  }

  /* Items du ticker */
  const tickerItems = [
    tObj.home.marqueeDiscount(),
    tObj.home.marqueeCourses(),
    tObj.home.marqueeSatisfaction(),
    tObj.home.marqueeCertificates(),
    tObj.home.marqueeLearn(),
    tObj.home.marqueePractical(),
    tObj.home.marqueeInstructors(),
    tObj.home.marqueeLifetime(),
  ]

  /* Stats marquee */
  const stats = [
    { num: "240+", label: tObj.home.coursesAvailable() },
    { num: "12K", label: tObj.home.studentsOnline() },
    { num: "96%", label: tObj.home.marqueeSatisfaction() },
    { num: "24/7", label: tObj.home.atYourPace() },
  ]

  return (
    <div className="bg-[#FAFBFD] text-[#1B2240] font-sans overflow-x-hidden">
      {/* ============================================================
       *  2. HERO ÉDITORIAL — image full-bleed, hauteur écran
       * ============================================================ */}
      <section className="relative h-screen min-h-[640px] w-full flex items-center overflow-hidden">
        {/* Image de fond */}
        <div className="absolute inset-0 z-0">
          <img
            src="/images/banner.jpg"
            alt={tObj.home.onlineStudent()}
            className="absolute inset-0 w-full h-full object-cover scale-105 animate-[hero-zoom_18s_ease-in-out_infinite_alternate]"
          />
          {/* Overlay uni léger — lisibilité du texte */}
          {/* Grille de pointillés subtile */}
          <div
            className="absolute inset-0 opacity-[0.07] pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)",
              backgroundSize: "28px 28px",
            }}
          />
        </div>

        {/* Contenu */}
        <div className="relative z-20 edito-container py-10 sm:py-14 w-full flex flex-col justify-between">
          <div className="flex flex-col items-center text-center space-y-5 sm:space-y-6">
            <h1 className="reveal delay-1 font-display text-white tracking-tight text-balance leading-[0.95] text-[clamp(3rem,7vw,6rem)] drop-shadow-[0_4px_30px_rgba(0,0,0,0.4)]" style={{ color: 'white' }}>
              Nouvelle <span className="text-[#E8C547]">plate-forme</span>
              <br />
              d'apprentissage
            </h1>

            <p className="reveal delay-2 text-white/85 text-base sm:text-lg max-w-2xl leading-relaxed">
              {tObj.home.heroDescription()}
            </p>

            {/* Search + CTA */}
            <div className="reveal delay-3 flex flex-col sm:flex-row gap-3 max-w-2xl w-full">
              <div className="flex-1 border border-white/30 bg-white/95 backdrop-blur-sm rounded-none focus-within:border-[#E8C547] transition-colors">
                <SearchBar
                  placeholder={tObj.home.searchPlaceholder()}
                  onSearch={handleSearch}
                  onChange={setSearchTerm}
                  variant="hero"
                  showSearchButton={false}
                />
              </div>
              <button
                onClick={handleExploreCourses}
                className="shimmer-cta group inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#E8C547] text-[#203a8a] font-medium tracking-wide hover:bg-[#d4af3a] transition-colors whitespace-nowrap"
              >
                <span>{tObj.home.exploreCourses()}</span>
                <ArrowUpRight className="w-5 h-5 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </button>
            </div>
          </div>
        </div>

        {/* Stats marquee at bottom of hero - full width */}
        <div className="absolute bottom-0 left-0 right-0 z-30">
          <div className="bg-[#203a8a] text-white py-4 overflow-hidden relative">
            <div className="absolute inset-0 opacity-[0.06]"
                 style={{
                   backgroundImage:
                     "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)",
                   backgroundSize: "24px 24px",
                 }} />
            <div className="stats-marquee relative">
              {[...stats, ...stats, ...stats].map((s, i) => (
                <div key={i} className="flex items-center gap-6 px-2">
                  <span className="font-display text-2xl sm:text-4xl lg:text-5xl text-[#E8C547] leading-none">
                    {s.num}
                  </span>
                  <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.3em] text-white/70 max-w-[18ch] whitespace-normal">
                    {s.label}
                  </span>
                  <span className="text-white/30 font-display text-2xl sm:text-4xl">·</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Animations locales hero */}
        <style>{`
          @keyframes hero-zoom {
            0%   { transform: scale(1.05) translateY(0); }
            100% { transform: scale(1.12) translateY(-1.5%); }
          }
          @keyframes scroll-line {
            0%   { transform: scaleY(0); transform-origin: top; }
            50%  { transform: scaleY(1); transform-origin: top; }
            51%  { transform: scaleY(1); transform-origin: bottom; }
            100% { transform: scaleY(0); transform-origin: bottom; }
          }
        `}</style>
      </section>


      {/* ============================================================
       *  2bis. VIDÉO DE PRÉSENTATION — éditoriale
       * ============================================================ */}
      <section className="rule-bot bg-[#FAFBFD]">
        <div className="edito-container py-10 sm:py-12">
          <div className="grid grid-cols-12 gap-8 mb-8 items-end">
            <div className="col-span-12 lg:col-span-5 reveal">
              <Eyebrow>Présentation · Vidéo</Eyebrow>
              <h2 className="font-display text-3xl sm:text-4xl text-[#203a8a] mt-3 leading-tight">
                Découvrez la plateforme en images
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-6 lg:col-start-7 reveal delay-1">
              <p className="text-[#1B2240]/70 text-base sm:text-lg max-w-md">
                Une visite guidée de l'expérience d'apprentissage AY Solutions Academy.
              </p>
            </div>
          </div>

          <div className="reveal delay-2 relative max-w-4xl mx-auto">
            {/* Cadre éditorial */}
            <div className="relative border border-[#D5DAE3] bg-[#1B2240] shadow-[0_30px_80px_-30px_rgba(27,34,64,0.35)]">
              <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
                <iframe
                  src="https://www.youtube.com/embed/V7OaIX3SPe0?modestbranding=1&rel=0&autoplay=1&mute=1&loop=1&playlist=V7OaIX3SPe0&controls=1&showinfo=0"
                  title="Vidéo de présentation AY Solutions Academy"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute top-0 left-0 w-full h-full"
                />
              </div>
            </div>

            {/* Coin doré décoratif */}
            <div className="hidden sm:block absolute -top-3 -left-3 w-12 h-12 bg-[#E8C547] z-[-1]" />
            <div className="hidden sm:block absolute -bottom-3 -right-3 w-12 h-12 border-2 border-[#203a8a] z-[-1]" />
          </div>
        </div>
      </section>

      {/* ============================================================
       *  5. COMMENT ÇA MARCHE — timeline éditoriale numérotée
       * ============================================================ */}
      <section className="rule-bot bg-[#F1F3F8]/40">
        <div className="edito-container py-12 sm:py-16">
          <div className="grid grid-cols-12 gap-8 mb-8">
            <div className="col-span-12 lg:col-span-4 reveal">
              <Eyebrow>Méthode</Eyebrow>
              <h2 className="font-display text-4xl sm:text-5xl text-[#203a8a] mt-3 leading-tight">
                {tObj.home.howItWorks()}
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-7 lg:col-start-6 reveal delay-1 flex items-end">
              <p className="text-[#1B2240]/70 text-lg max-w-xl">
                {tObj.home.howItWorksSubtitle()}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-14">
              {[
                { n: "01", t: tObj.home.step1Title(), d: tObj.home.step1Description(), Icon: "/images/graduation-hat.png" },
                { n: "02", t: tObj.home.step2Title(), d: tObj.home.step2Description(), Icon: "/images/television-icon.png" },
                { n: "03", t: tObj.home.step3Title(), d: tObj.home.step3Description(), Icon: "/images/certificate.png" },
              ].map((s, i) => (
                <div key={i} className={`reveal delay-${i + 1}`}>
                  <div className="flex items-center gap-4 mb-6">
                    <img src={s.Icon} alt="" className="w-10 h-10 object-contain" />
                  </div>
                  <h3 className="font-display text-2xl sm:text-3xl text-[#203a8a] leading-tight mb-3">
                    {s.t}
                  </h3>
                  <p className="text-[#1B2240]/70 leading-relaxed">{s.d}</p>
                </div>
              ))}
            </div>
        </div>
      </section>

      {/* ============================================================
       *  6. PROGRAMMES — mosaïque éditoriale (1 feature + 4 small)
       * ============================================================ */}
      <section className="rule-bot">
        <div className="edito-container py-12 sm:py-16">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 gap-6">
            <div className="reveal">
              <Eyebrow>Catalogue · Sélection</Eyebrow>
              <h2 className="font-display text-4xl sm:text-5xl text-[#203a8a] mt-3 leading-tight max-w-xl">
                {tObj.home.recommendedForYou()}
              </h2>
              <p className="text-[#1B2240]/70 mt-3 max-w-md">
                {tObj.home.recommendedForYouSubtitle()}
              </p>
            </div>
            <Link
              to="/courses"
              className="reveal delay-1 group inline-flex items-center gap-2 self-start sm:self-end font-medium text-[#203a8a] hover:gap-4 transition-all"
            >
              <span className="gold-underline">{tObj.home.viewAllCourses()}</span>
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>

          {loadingCourses ? (
            <div className="py-16 text-center">
              <div className="inline-block w-10 h-10 border-2 border-[#203a8a] border-t-transparent rounded-full animate-spin" />
              <p className="mt-4 text-sm uppercase tracking-[0.25em] text-[#6B7280]">
                {tObj.home.loadingCourses()}
              </p>
            </div>
          ) : recommendedCourses.length === 0 ? (
            <div className="py-16 text-center border-y border-[#D5DAE3]">
              <BookOpen className="w-10 h-10 text-[#D5DAE3] mx-auto mb-4" />
              <h3 className="font-display text-2xl text-[#203a8a]">
                {tObj.home.noRecommendedCourses()}
              </h3>
              <p className="text-[#1B2240]/60 mt-2">
                {tObj.home.noRecommendedCoursesDesc()}
              </p>
            </div>
          ) : (
            <div className="reveal">
              <CourseGrid courses={recommendedCourses} showPrice={false} />
            </div>
          )}
        </div>
      </section>

      {/* ============================================================
       *  7. POURQUOI NOUS — grille de stickers inclinés
       * ============================================================ */}
      <section className="rule-bot bg-[#F1F3F8]/50 relative overflow-hidden">
        {/* grain decoratif */}
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
             style={{
               backgroundImage:
                 "radial-gradient(circle at 1px 1px, #1B2240 1px, transparent 0)",
               backgroundSize: "4px 4px",
             }} />
        <div className="edito-container py-12 sm:py-16 relative">
          <div className="grid grid-cols-12 gap-10 lg:gap-14 items-start">
            {/* Colonne gauche — image illustration */}
            <div className="col-span-12 lg:col-span-5 reveal lg:sticky lg:top-24">
              <div className="relative">
                <div className="relative overflow-hidden border border-[#D5DAE3] shadow-[0_30px_80px_-30px_rgba(27,34,64,0.35)] aspect-[4/5]">
                  <img
                    src="/images/Photo-de-fam.jpg"
                    alt={tObj.home.onlineStudent()}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] hover:scale-105"
                  />
                  {/* badge flottant doré */}
                  <div className="absolute bottom-5 left-5 right-5 bg-[#E8C547] text-[#203a8a] p-4 flex items-center gap-3 shadow-xl">
                    <Clock className="w-7 h-7 flex-shrink-0" />
                    <div>
                      <p className="font-display text-base leading-tight">
                        {tObj.home.coursesAvailable()}
                      </p>
                      <Eyebrow className="!text-[#203a8a]/70 !text-[10px] mt-0.5">
                        {tObj.home.available24h()}
                      </Eyebrow>
                    </div>
                  </div>
                </div>
                {/* Coins déco */}
                <div className="hidden sm:block absolute -top-3 -left-3 w-12 h-12 bg-[#203a8a] z-[-1]" />
                <div className="hidden sm:block absolute -bottom-3 -right-3 w-12 h-12 border-2 border-[#E8C547]" />
              </div>
            </div>

            {/* Colonne droite — eyebrow + titre + liste */}
            <div className="col-span-12 lg:col-span-7 space-y-10">
              <div className="reveal delay-1">
                <Eyebrow>Différence</Eyebrow>
                <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#203a8a] mt-3 leading-[1.05]">
                  {tObj.home.whyChooseUs()}
                </h2>
                <p className="text-[#1B2240]/70 text-lg max-w-xl mt-5">
                  {tObj.home.whyChooseUsSubtitle()}
                </p>
              </div>

              <ul className="divide-y divide-[#D5DAE3] border-y border-[#D5DAE3]">
                {[
                  { Icon: Award, t: tObj.home.recognizedCertifications(), d: tObj.home.recognizedCertificationsDesc() },
                  { Icon: Clock, t: tObj.home.totalFlexibility(),         d: tObj.home.totalFlexibilityDesc() },
                  { Icon: Users, t: tObj.home.qualifiedTrainers(),        d: tObj.home.qualifiedTrainersDesc() },
                ].map((it, i) => (
                  <li
                    key={i}
                    className={`reveal delay-${i + 1} group grid grid-cols-12 gap-4 sm:gap-6 py-6 sm:py-7 items-start`}
                  >
                    <div className="col-span-2 sm:col-span-1 flex justify-center">
                      <div className="w-11 h-11 rounded-full border border-[#203a8a] flex items-center justify-center text-[#203a8a] group-hover:bg-[#203a8a] group-hover:text-white transition-colors">
                        <it.Icon className="w-5 h-5" />
                      </div>
                    </div>
                    <div className="col-span-12 sm:col-span-10">
                      <h3 className="font-display text-xl sm:text-2xl leading-tight text-[#203a8a]">
                        <span className="gold-underline">{it.t}</span>
                      </h3>
                      <p className="text-[#1B2240]/70 mt-2 leading-relaxed">{it.d}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
       *  8. TÉMOIGNAGES — marquee polaroïds
       * ============================================================ */}
      <section className="rule-bot py-12 sm:py-16 overflow-hidden">
        <div className="edito-container mb-8">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-5 reveal">
              <Eyebrow>Voix · Communauté</Eyebrow>
              <h2 className="font-display text-4xl sm:text-5xl text-[#203a8a] mt-3 leading-tight">
                {tObj.home.testimonials()}
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-6 lg:col-start-7 reveal delay-1 flex items-end">
              <p className="text-[#1B2240]/70 text-lg max-w-md">
                {tObj.home.testimonialsSubtitle()}
              </p>
            </div>
          </div>
        </div>

        <div className="edito-ticker-track relative">
          {/* fades sur les bords */}
          <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-[#FAFBFD] to-transparent z-10" />
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-[#FAFBFD] to-transparent z-10" />

          <div className="edito-ticker" style={{ animationDuration: "60s" }}>
            {testimonials.length > 0 ? testimonials.map((tm, i) => (
              <article
                key={tm.id || i}
                className="polaroid w-[320px] sm:w-[380px] flex-shrink-0"
                style={{ transform: `rotate(${tm.tilt || 0}deg)` }}
              >
                <Quote className="w-6 h-6 text-[#E8C547] mb-2" />
                <p className="font-display text-lg sm:text-xl text-[#1B2240] leading-snug italic">
                  « {tm.content || tm.t} »
                </p>
                <div className="mt-5 pt-4 border-t border-[#D5DAE3] flex items-center gap-3">
                  {tm.authorAvatar ? (
                    <img src={tm.authorAvatar} alt={tm.authorName} className="w-11 h-11 rounded-full object-cover" />
                  ) : (
                    <div className="w-11 h-11 rounded-full bg-[#203a8a] text-white flex items-center justify-center font-display text-lg">
                      {tm.authorName?.charAt(0) || "T"}
                    </div>
                  )}
                  <div>
                    <p className="font-display text-base text-[#203a8a] leading-tight">{tm.authorName || tm.n}</p>
                    <Eyebrow className="!text-[10px]">{tm.authorRole || tm.r}</Eyebrow>
                  </div>
                  <div className="ml-auto flex gap-0.5">
                    {[...Array(5)].map((_, idx) => (
                      <Star key={idx} className={`w-3.5 h-3.5 ${idx < (tm.rating || 5) ? "fill-[#E8C547] text-[#E8C547]" : "text-gray-300"}`} />
                    ))}
                  </div>
                </div>
              </article>
            )) : [
              { n: tObj.home.testimonial1Name(), r: tObj.home.testimonial1Role(), t: tObj.home.testimonial1Text(), tilt: 0 },
              { n: tObj.home.testimonial2Name(), r: tObj.home.testimonial2Role(), t: tObj.home.testimonial2Text(), tilt: 0 },
              { n: tObj.home.testimonial3Name(), r: tObj.home.testimonial3Role(), t: tObj.home.testimonial3Text(), tilt: 0 },
              { n: tObj.home.testimonial1Name(), r: tObj.home.testimonial1Role(), t: tObj.home.testimonial1Text(), tilt: 0 },
              { n: tObj.home.testimonial2Name(), r: tObj.home.testimonial2Role(), t: tObj.home.testimonial2Text(), tilt: 0 },
              { n: tObj.home.testimonial3Name(), r: tObj.home.testimonial3Role(), t: tObj.home.testimonial3Text(), tilt: 0 },
            ].map((tm, i) => (
              <article
                key={i}
                className="polaroid w-[320px] sm:w-[380px] flex-shrink-0"
                style={{ transform: `rotate(${tm.tilt}deg)` }}
              >
                <Quote className="w-6 h-6 text-[#E8C547] mb-2" />
                <p className="font-display text-lg sm:text-xl text-[#1B2240] leading-snug italic">
                  « {tm.t} »
                </p>
                <div className="mt-5 pt-4 border-t border-[#D5DAE3] flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-[#203a8a] text-white flex items-center justify-center font-display text-lg">
                    {tm.n.charAt(0)}
                  </div>
                  <div>
                    <p className="font-display text-base text-[#203a8a] leading-tight">{tm.n}</p>
                    <Eyebrow className="!text-[10px]">{tm.r}</Eyebrow>
                  </div>
                  <div className="ml-auto flex gap-0.5">
                    {[...Array(5)].map((_, idx) => (
                      <Star key={idx} className="w-3.5 h-3.5 fill-[#E8C547] text-[#E8C547]" />
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
       *  9. CTA FINAL — full-bleed accent gold
       * ============================================================ */}
      <section className="bg-[#E8C547] text-[#203a8a] relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{
            backgroundImage:
              "repeating-linear-gradient(45deg, #203a8a 0, #203a8a 1px, transparent 1px, transparent 24px)",
          }}
        />
        <div className="edito-container py-14 sm:py-20 relative">
          <div className="grid grid-cols-12 gap-8 items-end">
            <div className="col-span-12 lg:col-span-9 reveal">
              <Eyebrow className="!text-[#203a8a]/70">Rejoignez-nous · 2026</Eyebrow>
              <h2 className="font-display text-balance leading-[0.95] mt-4 text-[clamp(2.5rem,7vw,6.5rem)]">
                Prêt(e) à écrire le prochain chapitre
                <span className="italic"> de votre carrière</span> ?
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-3 reveal delay-1 flex lg:justify-end">
              <Link
                to="/courses"
                className="shimmer-cta group inline-flex items-center gap-3 px-8 py-5 bg-[#203a8a] text-white hover:bg-[#1a2f6e] transition-colors text-lg"
              >
                <span>Commencer</span>
                <ArrowUpRight className="w-6 h-6 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </div>
          </div>

          <div className="mt-16 pt-8 border-t border-[#203a8a]/20 grid grid-cols-2 sm:grid-cols-4 gap-6 reveal delay-2">
            {[
              { l: tObj.home.qualityCourses() },
              { l: tObj.home.certificatesIncluded() },
              { l: tObj.home.atYourPace() },
              { l: tObj.home.marqueeInstructors() },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="w-2 h-2 bg-[#203a8a] rounded-full" />
                <Eyebrow className="!text-[#203a8a]/80">{f.l}</Eyebrow>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
